import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useRealtime } from '../context/RealtimeContext';
import { canModerateSession } from '../utils/permissions';
import usePolling from './usePolling';

export const useDiscussion = (id) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { subscribe, unsubscribe } = useRealtime();
  
  const [discussion, setDiscussion] = useState(null);
  const [comments, setComments] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const reactionTimeoutRef = useRef({});

  const checkAccess = useCallback((data, unlockedStatus) => {
    if (!data) return;
    const settings = data.settings || {};
    const privacy = data.privacy || settings.privacy || 'public';
    
    if (user && (
      String(data.author_id) === String(user.id) || 
      user.role === 'platform_admin'
    )) {
      setIsUnlocked(true);
      setIsRestricted(false);
      setShowPasswordPrompt(false);
      return;
    }

    if (privacy === 'public') {
      setIsUnlocked(true);
      setIsRestricted(false);
      return;
    }

    if (data.has_password || settings.password) {
      if (!unlockedStatus) {
        setShowPasswordPrompt(true);
        setIsRestricted(false);
      } else {
        setIsUnlocked(true);
        setIsRestricted(false);
      }
    } else {
      setIsUnlocked(true);
      setIsRestricted(false);
    }
  }, [user]);

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      const [discData, commentsData] = await Promise.all([
        api.get(`/discussions/${id}`),
        api.get(`/comments?commentable_id=${id}&commentable_type=discussion`)
      ]);
      setDiscussion(discData);
      setComments(Array.isArray(commentsData?.data) ? commentsData.data : (Array.isArray(commentsData) ? commentsData : []));
      
      const unlockedDiscussions = JSON.parse(sessionStorage.getItem('unlocked_discussions') || '[]');
      checkAccess(discData, unlockedDiscussions.includes(id));
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch discussion data', err);
      if (err?.response?.status === 403 || err?.response?.status === 401) {
        setShowPasswordPrompt(true);
      }
      setLoading(false);
    }
  }, [id, checkAccess]);

  usePolling(fetchData, 5000, [id]);

  useEffect(() => {
    fetchData();
    
    if (id) {
      subscribe(`discussion.${id}.typing`);
      subscribe(`discussion.${id}.reactions`);
      
      api.post(`/discussions/${id}/visit`).catch(() => {});
    }

    const handleReactionEvent = (e) => {
      const { emoji, x, entity_id, entity_type } = e.detail;
      if (entity_type === 'discussion' && String(entity_id) === String(id)) {
        showReaction(emoji, x);
      }
    };

    window.addEventListener('room-reaction', handleReactionEvent);

    return () => {
      if (id) {
        unsubscribe(`discussion.${id}.typing`);
        unsubscribe(`discussion.${id}.reactions`);
      }
      window.removeEventListener('room-reaction', handleReactionEvent);
      Object.values(reactionTimeoutRef.current).forEach(clearTimeout);
    };
  }, [id, fetchData, subscribe, unsubscribe]);

  const showReaction = (emoji, x) => {
    const rid = Date.now() + Math.random();
    setReactions(prev => {
      const newReactions = [...prev, { id: rid, emoji, x }];
      return newReactions.slice(-20);
    });
    reactionTimeoutRef.current[rid] = setTimeout(() => {
      setReactions(prev => prev.filter(r => r.id !== rid));
      delete reactionTimeoutRef.current[rid];
    }, 2000);
  };

  const handleReaction = async (emoji) => {
    const x = Math.random() * 80 + 10;
    showReaction(emoji, x);
    try {
      await api.post(`/discussions/${id}/react`, { emoji, x });
    } catch (err) {
      console.warn('Failed to broadcast reaction', err);
    }
  };

  const handleUnlock = () => {
    setIsUnlocked(true);
    setShowPasswordPrompt(false);
    const unlocked = JSON.parse(sessionStorage.getItem('unlocked_discussions') || '[]');
    if (!unlocked.includes(id)) {
      unlocked.push(id);
      sessionStorage.setItem('unlocked_discussions', JSON.stringify(unlocked));
    }
  };

  const isModerator = canModerateSession(user, id, discussion?.community_id, discussion);

  return {
    discussion,
    comments,
    setComments,
    reactions,
    isUnlocked,
    isRestricted,
    showPasswordPrompt,
    loading,
    handleReaction,
    handleUnlock,
    isModerator,
    fetchData,
    activeParticipants: discussion?.active_participants || []
  };
};
